// src/utils/tokenManager.ts - 终极兼容版（无只读属性修改）
class TokenManager {
  private appVersion: string;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private expiresUtc: Date | null = null;
  private isRefreshing = false;
  private queue: Array<{ resolve: (t: string) => void; reject: (e: any) => void }> = [];
  private xhrPatched = false;
  private whiteListPaths = [
    '/api/login',
    '/api/login/refresh',
    '/api/login/CloudVerify',
    '/api/Share/Access',
    '/getaddress/ip/local/geo/v1/district',
  ];
  private readonly PRE_REFRESH_MS = 0 * 60 * 1000;
  private readonly LOGIN_PATH = '/'; // 统一登录页路径
  // 动态生成存储Key：pacs_access_版本号（例如 pacs_access_0.0.1）
  public get STORAGE_KEY(): string {
    return `mobile_pacs_access_${this.appVersion}`; // 替换点号避免特殊字符问题，可选
    // 若不需要替换点号，直接 return `pacs_access_${this.appVersion}`;
  }

  // 新增：错误通知回调（供业务层注册）
  private errorNotify?: (message: string) => void;

  // 新增：注册错误通知方法（业务代码调用）
  public registerErrorNotify(callback: (message: string) => void) {
    this.errorNotify = callback;
  }

  constructor() {
    this.appVersion = (window as any).config?.version || '1.0.0'; // 兜底版本号
    this.loadToken();
    this.installAllInterceptors();
    this.installLoginPageGuard();
  }

  private installLoginPageGuard() {
    // 监听页面跳转/加载，若当前页面是登录页且在iframe中，强制顶层跳转
    const checkLoginPageInIframe = () => {
      if (
        window.location.pathname === this.LOGIN_PATH &&
        window.top &&
        window.top !== window.self
      ) {
        this.redirectToLogin();
      }
    };

    // 监听页面加载完成
    if (typeof document !== 'undefined') {
      window.addEventListener('load', checkLoginPageInIframe);
      // 监听路由变化（SPA场景）
      window.addEventListener('popstate', checkLoginPageInIframe);
      window.addEventListener('hashchange', checkLoginPageInIframe);
    }
  }

  public loadToken() {
    const storageStr = localStorage.getItem(this.STORAGE_KEY);
    if (storageStr) {
      try {
        const storageObj = JSON.parse(storageStr);
        this.accessToken = storageObj.atk_v2 || null;
        this.refreshToken = storageObj.refreshToken || null;
        if (storageObj.atk_expires) {
          this.expiresUtc = new Date(storageObj.atk_expires);
        } else {
          this.parseExpiryFromToken();
        }
      } catch (e) {}
    }
  }

  private parseExpiryFromToken() {
    if (!this.accessToken) return;
    try {
      const payload = JSON.parse(atob(this.accessToken.split('.')[1]));
      if (payload.exp) {
        this.expiresUtc = new Date(payload.exp * 1000);
        // 更新存储对象中的过期时间
        this.updateStorage(obj => {
          obj.atk_expires = this.expiresUtc?.toISOString() || null;
        });
      }
    } catch (e) {
      console.warn('JWT 解析失败，无法获取过期时间');
    }
  }

  /** 辅助方法：更新localStorage中的存储对象 */
  private updateStorage(
    updater: (obj: {
      atk_v2: string | null;
      atk_expires: string | null;
      refreshToken: string | null;
    }) => void
  ) {
    // 先读取现有对象，若无则初始化空对象
    const storageStr =
      localStorage.getItem(this.STORAGE_KEY) ||
      '{"atk_v2":null,"atk_expires":null,"refreshToken":null}';
    try {
      const storageObj = JSON.parse(storageStr);
      // 执行更新逻辑
      updater(storageObj);
      // 保存回localStorage
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(storageObj));
    } catch (e) {
      console.error(`更新${this.STORAGE_KEY}失败`, e);
    }
  }

  private installXHRInterceptor() {
    if (this.xhrPatched) return;

    const origOpen = XMLHttpRequest.prototype.open;
    const origSend = XMLHttpRequest.prototype.send;
    const origSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;
    const self = this;
    const requestHeaders = new WeakMap<XMLHttpRequest, Map<string, string>>();
    let refreshTokenPromise: Promise<string> | null = null;

    const isTokenExpired = () => {
      if (!self.accessToken || !self.expiresUtc) return false;
      const nowTimestamp = new Date().getTime();
      const nowPlus1Minute = new Date(nowTimestamp + self.PRE_REFRESH_MS);
      return nowPlus1Minute >= self.expiresUtc;
    };

    XMLHttpRequest.prototype.open = function (method: string, url: string | URL, ...args: any[]) {
      const urlStr = url instanceof URL ? url.toString() : url;
      (this as any)._requestUrl = urlStr;
      requestHeaders.set(this, new Map<string, string>());
      if (urlStr && (urlStr.includes('/api/studies') || urlStr.includes('/dicom-web'))) {
        // console.log('XHR 拦截到 DICOM Web 请求:', {
        //   method,
        //   url: urlStr,
        //   time: new Date().toLocaleTimeString(),
        // });
      }
      return origOpen.call(this, method, url, ...args);
    };

    XMLHttpRequest.prototype.setRequestHeader = function (name: string, value: string) {
      const headers = requestHeaders.get(this);
      if (headers) headers.set(name.toLowerCase(), value);
      return origSetRequestHeader.call(this, name, value);
    };

    XMLHttpRequest.prototype.send = async function (body) {
      const url = (this as any)._requestUrl;
      const xhr = this;

      // 核心：监听401响应，直接触发顶层跳转
      xhr.addEventListener('load', function () {
        if (xhr.status === 401) {
          console.log('XHR 401 响应，强制跳转登录页:', url);
          self.redirectToLogin();
        }
      });

      if (url && !self.isWhiteListRequest(url)) {
        if (isTokenExpired() && self.getCurrentToken()) {
          console.log('Token 即将过期/已过期，开始刷新 token:', url);
          if (!refreshTokenPromise) {
            refreshTokenPromise = self
              .refreshToken_func()
              .catch(error => {
                console.warn('Token 刷新失败:', error);
                throw error;
              })
              .finally(() => {
                refreshTokenPromise = null;
              });
          }
          try {
            await refreshTokenPromise;
          } catch (error) {
            console.error('Token 刷新失败，请求终止:', url);
            xhr.abort();
            return;
          }
        }

        const currentToken = self.getCurrentToken();
        if (currentToken) {
          const headers = requestHeaders.get(xhr);
          const hasAuthHeader = headers && headers.has('authorization');
          if (!hasAuthHeader) {
            origSetRequestHeader.call(xhr, 'Authorization', `Bearer ${currentToken}`);
            // console.log('XHR 请求已注入 Authorization 头:', url);
          } else {
            const existingAuthHeader = headers.get('authorization');
            const existingToken = existingAuthHeader?.replace('Bearer ', '');
            if (existingToken !== currentToken) {
              // console.log('检测到 token 已更新，更新 Authorization 头');
              origSetRequestHeader.call(xhr, 'Authorization', `Bearer ${currentToken}`);
            }
          }
        } else {
          console.warn('XHR 请求需要 token，但当前 token 为空:', url);
          self.redirectToLogin(); // 无token直接跳转
        }
      }

      return origSend.call(xhr, body);
    };

    const origAddEventListener = XMLHttpRequest.prototype.addEventListener;
    XMLHttpRequest.prototype.addEventListener = function (
      type: string,
      listener: EventListenerOrEventListenerObject,
      options?: boolean | AddEventListenerOptions
    ) {
      if (type === 'readystatechange') {
        const wrappedListener = (event: Event) => {
          if (
            this.readyState === 4 &&
            this.status === 401 &&
            !self.isWhiteListRequest((this as any)._requestUrl)
          ) {
            console.log('XHR 401 已拦截，强制跳转:', (this as any)._requestUrl);
          }
        };
        return origAddEventListener.call(this, type, wrappedListener, options);
      }
      return origAddEventListener.call(this, type, listener, options);
    };

    this.xhrPatched = true;
    // console.log('XMLHttpRequest 拦截器安装完成');
  }

  setToken(token: string, expiresUtc?: string | Date, refreshToken?: string) {
    this.accessToken = token;
    // 更新存储对象
    this.updateStorage(obj => {
      obj.atk_v2 = token;
      if (refreshToken) {
        obj.refreshToken = refreshToken;
      }
      if (expiresUtc) {
        const expiryDate = typeof expiresUtc === 'string' ? new Date(expiresUtc) : expiresUtc;
        obj.atk_expires = expiryDate.toISOString();
      }
    });

    // 更新内存中的过期时间
    if (expiresUtc) {
      const expiryDate = typeof expiresUtc === 'string' ? new Date(expiresUtc) : expiresUtc;
      this.expiresUtc = expiryDate;
    } else {
      this.parseExpiryFromToken();
    }
  }

  getCurrentToken(): string | null {
    return this.accessToken;
  }

  clearToken() {
    this.accessToken = null;
    this.refreshToken = null;
    this.expiresUtc = null;
    this.xhrPatched = false;
    localStorage.removeItem(this.STORAGE_KEY);
    sessionStorage.removeItem('user');
    this.redirectToLogin();
  }

  /**
   * 本分支面向手机端云胶片场景：token 失效时不整页跳转登录页 /，
   * 仅清凭证并派发 token:unauthorized，由各页面 ValidateModal 弹框验证。
   * （若需恢复「仅部分路由不跳转」，再改为按 pathname 判断。）
   */
  private shouldSuppressLoginRedirect() {
    return true;
  }

  private notifyUnauthorizedWithoutRedirect() {
    try {
      window.dispatchEvent(
        new CustomEvent('token:unauthorized', {
          detail: {
            path: window.location.pathname,
            reason: '401_or_token_cleared',
          },
        })
      );
    } catch (error) {}
  }

  /** 兼容所有浏览器的顶层跳转方法（不修改任何只读属性） */
  private redirectToLogin() {
    if (this.shouldSuppressLoginRedirect()) {
      this.accessToken = null;
      this.refreshToken = null;
      this.expiresUtc = null;
      localStorage.removeItem(this.STORAGE_KEY);
      sessionStorage.removeItem('user');
      this.notifyUnauthorizedWithoutRedirect();
      return;
    }

    try {
      // 场景1：同域iframe - 尝试用顶层窗口的location.href（兼容写法）
      if (window.top && window.top !== window.self) {
        // 安全检测：避免跨域操作报错
        try {
          // 先尝试访问顶层窗口的location（同域才会成功）
          const topLoc = top.window.location;
          if (topLoc) {
            // 用try-catch包裹，避免严格模式下报错
            try {
              topLoc.href = this.LOGIN_PATH;
            } catch (e) {
              // 若直接赋值失败，尝试通过form提交/script注入跳转（终极兜底）
              const topDoc = top.window.document;
              const form = topDoc.createElement('form');
              form.action = this.LOGIN_PATH;
              form.method = 'GET';
              topDoc.body.appendChild(form);
              form.submit();
            }
            // 清空当前iframe
            window.document.body.innerHTML = '';
            return;
          }
        } catch (crossErr) {
          console.log('跨域iframe，发送跳转通知:', crossErr);
          // 跨域：发送postMessage通知主窗口
          top.window.postMessage(
            { type: 'FORCE_LOGIN_REDIRECT', path: this.LOGIN_PATH },
            '*' // 若知道主域名，替换为具体域名（如https://your-domain.com）
          );
        }
      }

      // 场景2：非iframe/跨域iframe兜底 - 当前窗口跳转
      window.location.href = this.LOGIN_PATH;
    } catch (finalErr) {
      console.error('跳转登录页失败，终极兜底:', finalErr);
      // 终极兜底：通过新建窗口+关闭当前窗口（仅作最后尝试）
      const newWin = window.open(this.LOGIN_PATH, '_top');
      if (newWin) window.close();
    }
  }

  private isWhiteListRequest(url: string): boolean {
    if (!url) return false;
    let fullUrl = url;
    if (!url.startsWith('http')) {
      const base = (window as any).config?.servers?.baseUrl || '';
      fullUrl = base + url;
    }
    return this.whiteListPaths.some(path => fullUrl.includes(path));
  }

  private async refreshToken_func() {
    if (this.isRefreshing) {
      return new Promise<string>((resolve, reject) => this.queue.push({ resolve, reject }));
    }

    this.isRefreshing = true;

    try {
      const base = (window as any).config?.servers?.baseUrl || '';
      const formData = new FormData();
      const storageStr = localStorage.getItem(this.STORAGE_KEY);
      const oldRefreshToken = storageStr ? JSON.parse(storageStr).refreshToken : null;
      if (oldRefreshToken) formData.append('refreshToken', oldRefreshToken);

      const res = await fetch(`${base}/api/login/refresh`, { method: 'POST', body: formData });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('刷新失败，响应内容:', errorText);
        throw new Error(`Refresh failed: ${res.status} ${res.statusText}`);
      }

      const responseData = await res.json();
      const { accessToken, accessExpiresUtc, refreshToken } = responseData;
      if (!accessToken) throw new Error('刷新响应中没有 accessToken');

      this.setToken(accessToken, accessExpiresUtc, refreshToken);
      this.queue.forEach(p => p.resolve(accessToken));
      this.queue = [];
      return accessToken;
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : '身份验证失败，请重新登录';
      console.error('刷新异常:', e);
      // 核心：触发页面错误提示（优先于跳转）
      if (this.errorNotify) {
        this.errorNotify(errorMsg);
      }

      // 延迟跳转，让错误提示有时间显示（关键！）
      setTimeout(() => {
        this.clearToken(); // 跳转登录
      }, 2000); // 2秒后跳转，足够用户看到错误

      this.queue.forEach(p => p.reject(e));
      this.queue = [];
      throw e;
    } finally {
      this.isRefreshing = false;
    }
  }

  private installFetchInterceptor() {
    const orig = window.fetch;
    const self = this;
    let refreshTokenPromise: Promise<string> | null = null;

    const isTokenExpired = () => {
      if (!self.expiresUtc) return true;
      const nowTimestamp = new Date().getTime();
      const nowPlus1Minute = new Date(nowTimestamp + self.PRE_REFRESH_MS);
      return nowPlus1Minute >= self.expiresUtc;
    };

    window.fetch = async (input: RequestInfo, init?: RequestInit) => {
      const url = typeof input === 'string' ? input : input.url;

      if (self.isWhiteListRequest(url)) {
        return orig(input, {
          ...init,
          credentials: 'include',
          headers: this.cleanAuthHeader(init?.headers),
        });
      }

      if (isTokenExpired() && self.getCurrentToken()) {
        if (!refreshTokenPromise) {
          refreshTokenPromise = self
            .refreshToken_func()
            .catch(error => {
              throw error;
            })
            .finally(() => {
              refreshTokenPromise = null;
            });
        }
        try {
          await refreshTokenPromise;
        } catch (error) {
          console.error('Token 刷新失败，fetch请求终止:', url);
          throw new Error('Token refresh failed, fetch aborted');
        }
      }

      const token = self.getCurrentToken();
      const headers = new Headers(init?.headers || {});
      if (token) headers.set('Authorization', `Bearer ${token}`);

      const resp = await orig(input, { ...init, headers, credentials: 'include' });

      // 核心：拦截401响应，直接触发顶层跳转
      if (resp.status === 401) {
        console.log('Fetch 401 响应，强制跳转登录页:', url);
        self.redirectToLogin();
        // 返回空响应，阻止业务代码处理
        return new Response(JSON.stringify({ code: 401, message: 'Unauthorized' }), {
          status: 401,
          headers: { 'Content-Type': 'application/json' },
        });
      }

      return resp;
    };
  }

  private cleanAuthHeader(headers?: HeadersInit): HeadersInit {
    if (!headers) return {};
    const newHeaders = new Headers(headers);
    newHeaders.delete('Authorization');
    return newHeaders;
  }

  private installAllInterceptors() {
    this.installFetchInterceptor();
    this.installXHRInterceptor();
  }

  addWhiteListPath(path: string) {
    if (!this.whiteListPaths.includes(path)) this.whiteListPaths.push(path);
  }

  removeWhiteListPath(path: string) {
    this.whiteListPaths = this.whiteListPaths.filter(p => p !== path);
  }
}

// 启动实例
const instance = new TokenManager();

// 主窗口监听iframe的跳转通知（关键：主窗口必须加这个）
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      console.log('页面可见，token由拦截器前置判断处理');
    }
  });

  window.addEventListener('storage', e => {
    if (e.key === instance.STORAGE_KEY) {
      instance.loadToken();
    }
  });

  // 核心：主窗口监听所有iframe的跳转请求
  window.addEventListener('message', e => {
    // 安全验证：仅接受可信域名的消息（根据实际情况修改）
    // const trustedOrigins = ['https://your-domain.com', 'http://localhost:3000'];
    // if (!trustedOrigins.includes(e.origin)) return;

    if (e.data.type === 'FORCE_LOGIN_REDIRECT') {
      console.log('主窗口收到强制跳转通知，立即跳转登录页');
      // 主窗口直接跳转（无只读属性限制）
      window.location.reload(true); // true 表示强制从服务器重新加载，而非读取缓存
      // 清理所有iframe，防止残留
      document.querySelectorAll('iframe').forEach(iframe => {
        iframe.src = 'about:blank';
        iframe.remove();
      });
    }
  });
}

(window as any).tokenManager = instance;
export default instance;
